import { Place as SiteIcon } from "@mui/icons-material";
import { Box, Chip, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";

import { ACCENT_COLOR } from "../../theme";
import { ACCENT_TEXT } from "./ChainCardNode";
import { COLLAPSED_DASH, EDGE_GREY, TYPICAL_DASH } from "./ChainEdge";
import { PathColour, SKIP_EDGES_DRAWN } from "./chainModel";
import { PATH_COLOUR_VALUES } from "./pathColours";
import { ChainLens } from "./valueChainState";

// A short stretch of edge, as the canvas draws it: whole, dashed, or dotted
const Line: React.FC<{
  colour: string;
  dashed?: boolean;
  dotted?: boolean;
}> = ({ colour, dashed = false, dotted = false }) => (
  <Box
    component="svg"
    width={28}
    height={8}
    aria-hidden
    sx={{ flexShrink: 0, verticalAlign: "middle" }}
  >
    <line
      x1={0}
      y1={4}
      x2={28}
      y2={4}
      stroke={colour}
      strokeWidth={2}
      strokeDasharray={
        dashed ? COLLAPSED_DASH : dotted ? TYPICAL_DASH : undefined
      }
      strokeLinecap={dotted ? "round" : undefined}
    />
  </Box>
);

// A maker chip, as a card draws it
const MakerSwatch: React.FC<{ proven: boolean }> = ({ proven }) => (
  <Chip
    label="Maker"
    size="small"
    variant="outlined"
    sx={(theme: Theme) => ({
      height: 20,
      fontSize: "11.5px",
      ...(proven
        ? {
            borderColor: alpha(theme.palette.accent.main, 0.45),
            color: ACCENT_TEXT,
            backgroundColor: alpha(theme.palette.accent.main, 0.1),
          }
        : {}),
    })}
  />
);

const Entry: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="li"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
  >
    {children}
  </Box>
);

interface ChainLegendProps {
  lens: ChainLens;
  // The chain is read both ways: the focus view
  focus: boolean;
  // The colours of the intermediate modules drawn
  pathColours: readonly PathColour[];
}

/**
 * What the marks of the chain mean, under it: the maker chips of the lens
 * and the edges, or, in the focus view, the colours of the paths.
 */
const ChainLegend: React.FC<ChainLegendProps> = ({
  lens,
  focus,
  pathColours,
}) => {
  const entries: React.ReactNode[] = [];
  if (focus) {
    // The direct edge skips a column: not drawn for now, so not named
    if (SKIP_EDGES_DRAWN) {
      entries.push(
        <Entry key="direct">
          <Line colour={ACCENT_COLOR} />
          direct: the focus is stated inside the host itself
        </Entry>,
      );
    }
    if (pathColours.length > 0) {
      entries.push(
        <Entry key="paths">
          {pathColours.map((colour) => (
            <Line key={colour} colour={PATH_COLOUR_VALUES[colour]} />
          ))}
          one colour per intermediate module, so a branch can be followed to
          its hosts
        </Entry>,
      );
    }
    entries.push(
      <Entry key="typical">
        <Line colour={EDGE_GREY} dotted />
        typical host, from the backbone or an article · dotted
      </Entry>,
      <Entry key="columns">
        columns are steps away from the focus by longest path; a host is
        drawn once, in the furthest column it is reached in
      </Entry>,
    );
  } else {
    if (lens !== "jurisdiction") {
      entries.push(
        <Entry key="proven">
          <MakerSwatch proven />
          proven: its product is stated inside the level to the left
        </Entry>,
        <Entry key="capable">
          <MakerSwatch proven={false} />
          makes this kind; no such link is stated
        </Entry>,
      );
    }
    if (lens === "facilities") {
      entries.push(
        <Entry key="site">
          <SiteIcon sx={{ fontSize: 16, color: "link.main" }} />
          a production site on record; else the maker's country
        </Entry>,
      );
    }
    entries.push(
      <Entry key="stated">
        <Line colour={EDGE_GREY} />
        stated has_component edge, source-cited · hover shows the pairs
      </Entry>,
      <Entry key="typical">
        <Line colour={EDGE_GREY} dotted />
        typical host, from the backbone or an article · dotted
      </Entry>,
      <Entry key="collapsed">
        <Line colour={EDGE_GREY} dashed />
        more kinds, collapsed
      </Entry>,
    );
  }
  return (
    <Box
      component="ul"
      aria-label="Legend"
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 2.75,
        rowGap: 1,
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      {entries}
    </Box>
  );
};

export default ChainLegend;
